/**
 * Swap Execution Hook
 *
 * Handles executing a swap transaction via raw calldata (sendTransaction).
 * Parses the actual output amount from transaction logs.
 * Separated from quote fetching for cleaner state management.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAccount, useSendTransaction } from 'wagmi'
import { useWaitForTransactionReceiptUniversal } from '@/domain/hooks/useWaitForTransactionReceiptUniversal'
import { useQueryClient } from '@tanstack/react-query'
import { Address, erc20Abi, parseEventLogs } from 'viem'

export interface UseSwapExecutionParams {
  /** Target contract address */
  routerAddress?: Address
  /** Calldata for the swap */
  calldata?: `0x${string}`
  /** Expected output token address */
  outputToken?: Address
  /** Chain ID */
  chainId: number
  /** Whether the hook is enabled */
  enabled?: boolean
  /** Native value to send (for native token swaps) */
  value?: bigint
}

export type SwapStep = 'idle' | 'swapping' | 'swapped'

export interface SwapOutput {
  /** Actual output amount in base units */
  amount: string
  /** Output token address */
  tokenAddress: Address
}

export interface UseSwapExecutionResult {
  /** Current swap step */
  step: SwapStep
  /** Whether swapping is loading/pending */
  isLoading: boolean
  /** Execute swap function */
  executeSwap: () => void
  /** Reset the swap state */
  reset: () => void
  /** Any error that occurred */
  error: Error | null
  /** Transaction hash */
  hash?: `0x${string}`
  /** Whether swap was successful */
  isSuccess: boolean
  /** Actual output from the swap (parsed from logs) */
  output: SwapOutput | null
}

export function useSwapExecution({
  routerAddress,
  calldata,
  outputToken,
  chainId,
  enabled = true,
  value,
}: UseSwapExecutionParams): UseSwapExecutionResult {
  const { address } = useAccount()
  const queryClient = useQueryClient()
  const [step, setStep] = useState<SwapStep>('idle')
  const [output, setOutput] = useState<SwapOutput | null>(null)

  // Swap transaction — use sendTransaction to send raw calldata as-is
  const {
    sendTransaction,
    data: swapHash,
    isPending: isSwapPending,
    error: swapError,
    reset: resetSend,
  } = useSendTransaction()

  const {
    data: receipt,
    isLoading: isSwapConfirming,
    isSuccess: isSwapSuccess,
    isError: isSwapError,
    error: waitError,
  } = useWaitForTransactionReceiptUniversal({
    hash: swapHash,
    chainId,
  })

  // Track if transaction was submitted but reverted
  const [transactionReverted, setTransactionReverted] = useState(false)

  // Parse output amount from transaction receipt
  useEffect(() => {
    if (isSwapSuccess && receipt && address && outputToken && step === 'swapping') {
      try {
        // Parse Transfer events from the receipt
        const transferEvents = parseEventLogs({
          abi: erc20Abi,
          eventName: 'Transfer',
          logs: receipt.logs,
        })

        // Sum all Transfer events where:
        // - Token is the output token
        // - Recipient is the user's address
        // Note: DEX aggregators may route through multiple paths, resulting in multiple transfers
        const totalOutput = transferEvents
          .filter(
            (event) =>
              event.address.toLowerCase() === outputToken.toLowerCase() &&
              event.args.to?.toLowerCase() === address.toLowerCase(),
          )
          .reduce((sum, event) => sum + (event.args.value ?? 0n), 0n)

        if (totalOutput > 0n) {
          setOutput({
            amount: totalOutput.toString(),
            tokenAddress: outputToken,
          })
        }
        // If no Transfer events found, output remains null (swap might have output native tokens)
      } catch (error) {
        // Error parsing output - transaction still succeeded, just can't determine exact output
        console.warn('Failed to parse swap output from logs:', error)
      }

      setStep('swapped')
      setTransactionReverted(false)

      // Invalidate wallet balances after swap completes
      void queryClient.invalidateQueries({
        queryKey: ['walletTokenBalances', chainId, address],
      })
      void queryClient.invalidateQueries({
        queryKey: ['readContract'],
      })
      void queryClient.invalidateQueries({
        queryKey: ['readContracts'],
      })
    }
  }, [isSwapSuccess, receipt, step, address, outputToken, chainId, queryClient])

  // Handle transaction revert/failure
  useEffect(() => {
    if (swapHash && isSwapError && step === 'swapping') {
      setStep('idle')
      setTransactionReverted(true)
      resetSend()
    }
  }, [swapHash, isSwapError, step, resetSend])

  // Reset step if user rejects or write error occurs
  useEffect(() => {
    if (swapError && step === 'swapping') {
      setStep('idle')
      resetSend()
    }
  }, [swapError, step, resetSend])

  // Execute swap function — sends raw transaction matching how the swap page works
  const executeSwap = useCallback(() => {
    if (!address || !enabled || !routerAddress || !calldata) return
    setStep('swapping')
    setOutput(null)

    sendTransaction({
      to: routerAddress,
      data: calldata,
      value: value ?? 0n,
      chainId,
    })
  }, [address, enabled, routerAddress, calldata, value, sendTransaction, chainId])

  // Reset function
  const reset = useCallback(() => {
    setStep('idle')
    setTransactionReverted(false)
    setOutput(null)
    resetSend()
  }, [resetSend])

  const isLoading = isSwapPending || isSwapConfirming || step === 'swapping'

  // Combine all errors
  const combinedError = useMemo(() => {
    if (swapError) return swapError as Error
    if (waitError) return waitError as Error
    if (transactionReverted) return new Error('Transaction reverted on-chain. The swap failed.')
    return null
  }, [swapError, waitError, transactionReverted])

  return {
    step,
    isLoading,
    executeSwap,
    reset,
    error: combinedError,
    hash: swapHash,
    isSuccess: step === 'swapped',
    output,
  }
}
